/**
 * Логика связывания пар в упражнении «сопоставь слова».
 *
 * Вынесена из блока-компонента отдельно, потому что тихо ломается: на экране
 * выбор просто сбрасывается, а ответ уезжает. Так это и попало на боевой сервер —
 * ученик получил красными правильные ответы (17.08.2026).
 */

/** Кто с кем связан: левое слово → выбранный вариант. */
export type Picks = Record<string, string>;
/** Выбор, ждущий пары. */
export type Selection = { side: 'left' | 'right'; val: string } | null;

export type MatchState = { picks: Picks; sel: Selection };

/** Обратный указатель: вариант → левое слово, которое его заняло. */
export const rightOwner = (picks: Picks): Record<string, string> => {
  const m: Record<string, string> = {};
  for (const [l, r] of Object.entries(picks)) m[r] = l;
  return m;
};

/* ─────────────────────────────────────────────────────────────────────────────
 * Повторы в парах (решение владельца 13.10.2026, п. 2).
 *
 * Вся логика ниже ключуется ТЕКСТОМ: слово банка — ключ `picks`, строка — ключ
 * вердиктов. На проде (копия 13.10) два опубликованных задания с «hold → out» и
 * «hold → back»: после первой пары из банка исчезали ОБЕ плашки «hold», вторую
 * строку заполнить было нечем, задание не решалось.
 *
 * ⚠️ ПОВТОР ДЕЛАЕТСЯ РАЗЛИЧИМЫМ НЕВИДИМЫМ ХВОСТОМ, А НЕ НОМЕРАМИ. Второй «hold»
 * получает один U+200B, третий — два. На экране то же слово, ключи разные, и
 * формат ответа прежний — объект `{"слово":"строка"}`: его читают восстановление,
 * разбор куратора и gradeMatching на сервере (там Fold выбрасывает U+200B и пары
 * сверяются набором с повторами). Тот же хвост сервер ставит старому клиенту
 * (courses-service block_compat.go) — правило побайтно одно, иначе разойдутся
 * ключи сохранённого ответа и раздачи.
 *
 * ⚠️ СРАВНЕНИЕ — ПО СЛОВУ БЕЗ ХВОСТА: два «hold» взаимозаменяемы, разложенные
 * крест-накрест — верны.
 * ───────────────────────────────────────────────────────────────────────────── */

// Кодом знака, не буквой: невидимый символ в исходнике теряется при правке (13.10.2026).
const ZW = String.fromCharCode(0x200b);
const ZW_TAIL = new RegExp(`${ZW}+$`);

/** Пары с различимыми повторами: k-й повтор слова (и строки) — k знаков U+200B в конце. */
export function distinctPairs<P extends { left: string; right: string }>(pairs: P[]): P[] {
  const seenL = new Map<string, number>();
  const seenR = new Map<string, number>();
  return pairs.map((p) => {
    const l = seenL.get(p.left) ?? 0;
    const r = seenR.get(p.right) ?? 0;
    seenL.set(p.left, l + 1);
    seenR.set(p.right, r + 1);
    if (l === 0 && r === 0) return p;
    return { ...p, left: l ? p.left + ZW.repeat(l) : p.left, right: r ? p.right + ZW.repeat(r) : p.right };
  });
}

/**
 * Тот же хвост для простого списка слов — «распредели по колонкам» (`column_sort`):
 * ответ там словарь «слово → колонка», и два «berry» в разных колонках после
 * перезагрузки съезжали в одну (13.10.2026).
 */
export function distinctWords(words: string[]): string[] {
  const seen = new Map<string, number>();
  return words.map((w) => {
    const k = seen.get(w) ?? 0;
    seen.set(w, k + 1);
    return k ? w + ZW.repeat(k) : w;
  });
}

/** Слово без невидимого хвоста повтора — для сравнения «верно ли». */
export const baseLabel = (s: string): string => s.replace(ZW_TAIL, '');

/** Все строки связаны с верным по смыслу словом (повторы взаимозаменяемы). */
export const allPairsCorrect = (pairs: { left: string; right: string }[], picks: Picks): boolean => {
  const owner = rightOwner(picks);
  return pairs.length > 0 && pairs.every((p) => owner[p.right] !== undefined && baseLabel(owner[p.right]) === baseLabel(p.left));
};

/**
 * Один клик по карточке. Возвращает НОВОЕ состояние, исходное не трогает.
 *
 * Порядок веток здесь и есть суть: связывание идёт ПЕРВЫМ. Если сначала
 * проверять занятость, то клик по уже занятому варианту рвёт чужую пару и не
 * создаёт свою — ученик уверен, что связал, и идёт дальше.
 */
export function matchTap(state: MatchState, side: 'left' | 'right', val: string): MatchState {
  const { picks, sel } = state;

  // Выбрана противоположная сторона → связываем, даже если что-то из двух уже занято.
  if (sel && sel.side !== side) {
    const left = side === 'left' ? val : sel.val;
    const right = side === 'right' ? val : sel.val;
    const next: Picks = { ...picks };
    // Вариант живёт ровно в одной паре: прежнего владельца освобождаем.
    for (const k of Object.keys(next)) if (next[k] === right) delete next[k];
    next[left] = right;
    return { picks: next, sel: null };
  }

  // Повторный клик по уже выбранной карточке снимает выбор.
  if (sel && sel.val === val) return { picks, sel: null };

  // Клик по связанной карточке разрывает её пару И выбирает эту карточку.
  // Выбор здесь обязателен: иначе ученик, желая переназначить свою пару, кликает
  // по слову (пара рвётся, ничего не выбрано), потом по нужному варианту — и тот
  // разрывает уже ЧУЖУЮ пару вместо того, чтобы связаться. Так одна правка ответа
  // ломала две пары разом.
  const owner = side === 'left' ? (picks[val] ? val : undefined) : rightOwner(picks)[val];
  if (owner) {
    const next: Picks = { ...picks };
    delete next[owner];
    return { picks: next, sel: { side, val } };
  }

  return { picks, sel: { side, val } };
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Строки после проверки (30.09.2026, 329 + слово владельца «три попытки»).
 *
 * Экран: определение (правая сторона пары) — строка, слово (левая) — в поле
 * строки. Строка — это вариант `right`, поэтому всё ниже ключуется по нему.
 *
 * ⚠️ СБРОС — ОДНОЙ СТРОКИ. Прежнее «Ещё раз» стирало все пары, и ученик заново
 * раскладывал уже верные (решение владельца 28.09, п. 329). Теперь касание
 * красной строки возвращает в банк только её слово.
 *
 * ⚠️ ВЕРНОЕ СЛОВО — ТОЛЬКО ПОСЛЕ ТРЕТЬЕЙ ОШИБКИ НА СТРОКЕ. Раньше верное слово
 * стояло под каждой ошибкой, и сброс одной строки превратился бы в списывание.
 * Без подсказки совсем ученик мог застрять насмерть — после третьей неверной
 * попытки строка показывает верное слово и запирается.
 * ───────────────────────────────────────────────────────────────────────────── */

/** Сколько неверных проверок у строки до показа верного слова. */
export const MAX_ROW_TRIES = 3;

/**
 * Вердикт строки. `ok` — верно, заперта. `bad` — неверно, слово стоит, касание
 * строки его снимает. `revealed` — третья ошибка: показано верное слово, заперта.
 * Нет вердикта — строка открыта для ответа.
 */
export type RowVerdict = 'ok' | 'bad' | 'revealed';

export type RowState = {
  verdicts: Record<string, RowVerdict>;
  /** Неверных проверок по строке. */
  tries: Record<string, number>;
};

export const emptyRows = (): RowState => ({ verdicts: {}, tries: {} });

/** Слова банка: ещё без пары, в порядке раздачи. Выбранное слово из банка исчезает. */
export const bankWords = (lefts: string[], picks: Picks): string[] => lefts.filter((l) => !picks[l]);

/** Строка заперта до «Сбросить всё»: верная или с показанным ответом. */
export const rowLocked = (rows: RowState, right: string): boolean => {
  const v = rows.verdicts[right];
  return v === 'ok' || v === 'revealed';
};

/**
 * Проверка. Оцениваются только строки без вердикта: верные и запертые не трогаются,
 * а красная, которую ученик не сбрасывал, повторно попытку не тратит — он её не менял.
 */
export function gradeRows(pairs: { left: string; right: string }[], picks: Picks, rows: RowState): RowState {
  const owner = rightOwner(picks);
  const verdicts = { ...rows.verdicts };
  const tries = { ...rows.tries };
  for (const p of pairs) {
    const row = p.right;
    if (verdicts[row] || !owner[row]) continue;
    // По слову без хвоста повтора: два «hold» взаимозаменяемы (distinctPairs выше).
    if (baseLabel(owner[row]) === baseLabel(p.left)) {
      verdicts[row] = 'ok';
      continue;
    }
    tries[row] = (tries[row] ?? 0) + 1;
    verdicts[row] = tries[row] >= MAX_ROW_TRIES ? 'revealed' : 'bad';
  }
  return { verdicts, tries };
}

/**
 * Касание красной строки: её слово — в банк, вердикт снят, строка снова открыта.
 * Остальные пары и счётчики не меняются. Верную и запертую строку не трогает.
 */
export function resetRow(picks: Picks, rows: RowState, right: string): { picks: Picks; rows: RowState } {
  if (rows.verdicts[right] !== 'bad') return { picks, rows };
  const next: Picks = { ...picks };
  for (const k of Object.keys(next)) if (next[k] === right) delete next[k];
  const verdicts = { ...rows.verdicts };
  delete verdicts[right];
  return { picks: next, rows: { verdicts, tries: rows.tries } };
}

/** «Проверить» доступна: у каждой строки есть слово и хотя бы одна строка ещё не оценена. */
export function canCheck(rights: string[], picks: Picks, rows: RowState): boolean {
  if (rights.length === 0) return false;
  const owner = rightOwner(picks);
  return rights.every((r) => owner[r]) && rights.some((r) => !rows.verdicts[r]);
}

/** Задание закончено: у каждой строки окончательный вердикт — верно или показан ответ. */
export const allSettled = (rights: string[], rows: RowState): boolean =>
  rights.length > 0 && rights.every((r) => rowLocked(rows, r));
